import z from "zod";

export const timeframeSchema = z.union([
  z.object({
    op: z.literal("less_or_equal"),
    count: z.number(),
  }),
  z.object({
    op: z.literal("greater"),
    count: z.number(),
  }),
  z.object({
    op: z.literal("between"),
    from: z.string().date(),
    to: z.string().date(),
  }),
  z.object({
    op: z.literal("less"),
    date: z.string().date(),
  }),
  z.object({
    op: z.literal("greater"),
    date: z.string().date(),
  }),
  z.object({
    op: z.literal("equal"),
    date: z.string().date(),
  }),
]);

const audienceFilter_ListInSchema = z.object({
  type: z.literal("list"),
  id: z.string().optional(),
  cop: z.enum(["is", "is_not"]),
  op: z.literal("is"),
  not: z.boolean(),
  value: z.string().min(1),
  timeframe: timeframeSchema.optional(),
});
const audienceFilter_Prospect = z.object({
  type: z.literal("prospect"),
  id: z.string().optional(),
  cop: z.enum(["is", "is_not"]),
  op: z.enum(["is"]),
  not: z.boolean(),
});

// The reasoning sub-filter narrows an eligibility filter to one reason, and
// each value belongs to one side of the filter: every value carries its own
// eligibility in its predicate, so the side is fixed per value. Shared with the
// Filters.tsx dropdown (single source of truth); the superRefine below rejects
// a value paired with the side it does not belong to.
export const emailEligibleSuppressionValues = [
  "unsubscribed",
  "spam-complaint",
  "hard-bounce",
  "email-suppressed",
  "consent-not-given",
] as const;
export const emailEligibleSubscribedValues = [
  "subscribed",
  "not-subscribed",
] as const;
export const emailEligibleValues = [
  ...emailEligibleSuppressionValues,
  ...emailEligibleSubscribedValues,
] as const;
export type EmailEligibleValue = (typeof emailEligibleValues)[number];

// A timeframe matches on the date of the value's own reason. These two are
// defined by never having made a consent decision, so they have no date and
// Audiences rejects a timeframe on them.
export const emailEligibleUntimedValues: readonly EmailEligibleValue[] = [
  "not-subscribed",
  "consent-not-given",
];

/**
 * The two values for a profile that never made a consent decision. They are one
 * concept to a marketer, split by whether such a profile is eligible — which is
 * the site's opt-in requirement to decide. Audiences accepts both on any site
 * and leaves that match to the caller, so choosing between them is this app's
 * job; see the Filters.tsx sub-filter.
 */
export const emailEligibleNeverDecidedValues = {
  eligible: "not-subscribed",
  notEligible: "consent-not-given",
} as const;

/** Side a value belongs to. A value always travels as a positive predicate. */
export function emailEligibleValueCop(value: string): "is" | "is_not" {
  return (emailEligibleSubscribedValues as readonly string[]).includes(value)
    ? "is"
    : "is_not";
}

// SMS eligibility reasons explain ineligibility only, so each travels with
// cop "is_not". Never asked has no consent date, so it takes no timeframe.
export const smsEligibleValues = ["unsubscribed", "not-subscribed"] as const;
export type SmsEligibleValue = (typeof smsEligibleValues)[number];
export const smsEligibleUntimedValues: readonly SmsEligibleValue[] = [
  "not-subscribed",
];

const audienceFilter_EmailEligible = z.object({
  type: z.literal("email-eligible"),
  id: z.string().optional(),
  cop: z.enum(["is", "is_not"]),
  op: z.literal("is"),
  not: z.boolean(),
  value: z.enum(emailEligibleValues).optional(),
  timeframe: timeframeSchema.optional(),
});
const audienceFilter_SmsEligible = z.object({
  type: z.literal("sms-eligible"),
  id: z.string().optional(),
  cop: z.enum(["is", "is_not"]),
  op: z.literal("is"),
  not: z.boolean(),
  value: z.enum(smsEligibleValues).optional(),
  timeframe: timeframeSchema.optional(),
});
const audienceFilter_Email = z.object({
  type: z.literal("email"),
  id: z.string().optional(),
  cop: z.enum([
    "is",
    "is_not",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "is_blank",
    "is_not_blank",
  ]),
  op: z.enum(["is", "contains", "starts_with", "ends_with"]),
  not: z.boolean(),
  value: z.string(),
});
const audienceFilter_Phone = z.object({
  type: z.literal("phone"),
  id: z.string().optional(),
  cop: z.enum([
    "is",
    "is_not",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
    "is_blank",
    "is_not_blank",
  ]),
  op: z.enum(["is", "contains", "starts_with", "ends_with"]),
  not: z.boolean(),
  value: z.string(),
});
export const metadataCops = [
  "is",
  "is_not",
  "contains",
  "not_contains",
  "starts_with",
  "not_starts_with",
  "ends_with",
  "not_ends_with",
  "is_blank",
  "is_not_blank",
] as const;
export type MetadataCop = (typeof metadataCops)[number];

export const metadataNumericCops = [
  "num_equal",
  "num_not_equal",
  "num_greater_or_equal",
  "num_less",
  "num_greater",
  "num_less_or_equal",
] as const;
export type MetadataNumericCop = (typeof metadataNumericCops)[number];

const metadataAllCops = [...metadataCops, ...metadataNumericCops] as const;

const audienceFilter_Metadata = z.object({
  type: z.literal("metadata"),
  id: z.string().optional(),
  cop: z.enum(metadataAllCops),
  op: z.enum([
    "is",
    "contains",
    "starts_with",
    "ends_with",
    "equal",
    "greater_or_equal",
    "less_or_equal",
  ]),
  not: z.boolean(),
  key: z.string().min(1),
  value: z.string().default(""),
});

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function isMetadataNumericCop(cop: string): cop is MetadataNumericCop {
  return (metadataNumericCops as readonly string[]).includes(cop);
}

function shiftOperand(value: string, by: 1 | -1): string {
  const trimmed = value.trim();
  const n = Number(trimmed);
  if (trimmed === "" || !Number.isInteger(n)) return trimmed;
  return String(n + by);
}

export function metadataNumericCopToWire(
  cop: MetadataNumericCop,
  value: string,
): {
  op: "equal" | "greater_or_equal" | "less_or_equal";
  value: string;
  not: boolean;
} {
  switch (cop) {
    case "num_equal":
      return { op: "equal", value, not: false };
    case "num_not_equal":
      return { op: "equal", value, not: true };
    case "num_greater_or_equal":
      return { op: "greater_or_equal", value, not: false };
    case "num_less":
      return { op: "greater_or_equal", value, not: true };
    case "num_less_or_equal":
      return { op: "less_or_equal", value, not: false };
    case "num_greater":
      return { op: "less_or_equal", value, not: true };
    default: {
      const _exhaustive: never = cop;
      throw new Error(
        `Unsupported metadata numeric cop: ${String(_exhaustive)}`,
      );
    }
  }
}

function normalizeMetadataFilter(raw: unknown): unknown {
  if (!isPlainObject(raw) || raw.type !== "metadata") return raw;

  const rawCop = typeof raw.cop === "string" ? raw.cop : "";
  const op = typeof raw.op === "string" ? raw.op : "";
  const isNumeric =
    isMetadataNumericCop(rawCop) ||
    op === "equal" ||
    op === "greater_or_equal" ||
    op === "less_or_equal";

  if (!isNumeric) {
    if (!("count" in raw)) return raw;
    const rest = { ...raw };
    delete rest.count;
    return rest;
  }

  const rawNot = Boolean(raw.not);
  let cop: MetadataNumericCop;
  if (isMetadataNumericCop(rawCop)) {
    cop = rawCop === "num_equal" && rawNot ? "num_not_equal" : rawCop;
  } else if (op === "equal") {
    cop = rawNot ? "num_not_equal" : "num_equal";
  } else if (op === "less_or_equal") {
    cop = rawNot ? "num_greater" : "num_less_or_equal";
  } else {
    cop = rawNot ? "num_less" : "num_greater_or_equal";
  }

  const rawValue = typeof raw.value === "string" ? raw.value.trim() : "";
  const stored =
    rawValue !== ""
      ? rawValue
      : typeof raw.count === "number"
        ? String(raw.count)
        : "";

  const wasOffsetEncoded =
    (cop === "num_greater" || cop === "num_less_or_equal") &&
    op === "greater_or_equal";
  const wire = metadataNumericCopToWire(
    cop,
    wasOffsetEncoded ? shiftOperand(stored, -1) : stored,
  );

  return {
    type: "metadata",
    ...(typeof raw.id === "string" ? { id: raw.id } : {}),
    cop,
    op: wire.op,
    not: wire.not,
    key: typeof raw.key === "string" ? raw.key : "",
    value: wire.value,
  };
}

// A payload carrying op but no cop would otherwise take cop's default and be
// rejected by the pair check below, failing the whole listing response.
function normalizeRevenueValueFilter(raw: unknown): unknown {
  if (!isPlainObject(raw)) return raw;
  if (raw.type !== "revenue-value" || raw.cop !== undefined) return raw;
  const cop = revenueValueCops.find((c) => revenueValueCopOps[c] === raw.op);
  return cop ? { ...raw, cop } : raw;
}

function normalizeInCartFilter(raw: unknown): unknown {
  if (!isPlainObject(raw)) return raw;
  if (raw.type !== "in-cart" || raw.cop !== undefined) return raw;
  const not = raw.not === true;
  const cop = inCartCops.find(
    (c) => inCartCopWire[c].op === raw.op && inCartCopWire[c].not === not,
  );
  return cop ? { ...raw, cop, not } : raw;
}

const subFiltersTypes = new Set([
  "order-placed",
  "in-cart",
  "viewed-product",
  "high-intent-view",
]);

// The gateway sends `subFilters: []` on every filter, and a 1-entry list
// matches and hashes like `subFilter`, so the store keeps the list only for a
// product with variants.
function normalizeSubFilters(raw: unknown): unknown {
  if (
    !isPlainObject(raw) ||
    typeof raw.type !== "string" ||
    !subFiltersTypes.has(raw.type) ||
    !Array.isArray(raw.subFilters)
  ) {
    return raw;
  }
  const { subFilters, ...rest } = raw;
  if (subFilters.length === 0) return rest;
  if (subFilters.length === 1 && rest.subFilter === undefined) {
    return { ...rest, subFilter: subFilters[0] };
  }
  return raw;
}

// cop/not are UI-only; audiences written outside this app arrive without them.
const copDerivedTypes = new Set([
  "list",
  "prospect",
  "email-eligible",
  "sms-eligible",
  "email",
  "phone",
  "tags",
  "metadata",
  "country",
  "region",
  "city",
  "postal-code",
]);
const blankCopTypes = new Set([
  "email",
  "phone",
  "metadata",
  "country",
  "region",
  "city",
  "postal-code",
]);
const negatedCops: Record<string, string> = {
  is: "is_not",
  contains: "not_contains",
  starts_with: "not_starts_with",
  ends_with: "not_ends_with",
};

function deriveCop(raw: Record<string, unknown>): string | undefined {
  const { type, op, value } = raw;
  if (typeof type !== "string" || typeof op !== "string") return undefined;
  const not = raw.not === true;
  if (type === "email-eligible" && typeof value === "string" && value !== "") {
    return emailEligibleValueCop(value);
  }
  if (type === "sms-eligible" && typeof value === "string" && value !== "") {
    return "is_not";
  }
  const blank = blankCopTypes.has(type) && (value ?? "") === "";
  switch (op) {
    case "is_not_excl_blank":
      return blank ? "is_not_blank" : "is_not";
    case "does_not_contain_excl_blank":
      return "not_contains";
    case "is":
      if (blank) return not ? "is_not_blank" : "is_blank";
      break;
  }
  return not ? negatedCops[op] : op;
}

function normalizeMissingCopNot(raw: unknown): unknown {
  if (
    !isPlainObject(raw) ||
    typeof raw.type !== "string" ||
    !copDerivedTypes.has(raw.type)
  ) {
    return raw;
  }
  const withNot = raw.not === undefined ? { ...raw, not: false } : raw;
  if (withNot.cop !== undefined) return withNot;
  const cop = deriveCop(withNot);
  return cop === undefined ? withNot : { ...withNot, cop };
}

/** Runs before audienceFiltersSchema so discriminatedUnion still sees plain objects. */
function normalizeAudienceFiltersInput(raw: unknown): unknown {
  if (!isPlainObject(raw) || !Array.isArray(raw.groups)) return raw;
  return {
    ...raw,
    groups: raw.groups.map((group) => {
      if (!isPlainObject(group) || !Array.isArray(group.filters)) return group;
      return {
        ...group,
        filters: group.filters
          .map(normalizeMetadataFilter)
          .map(normalizeRevenueValueFilter)
          .map(normalizeInCartFilter)
          .map(normalizeSubFilters)
          .map(normalizeMissingCopNot),
      };
    }),
  };
}

const audienceFilter_ProfileCreated = z.object({
  type: z.literal("profile-created"),
  id: z.string().optional(),
  op: z.literal("is"),
  timeframe: timeframeSchema,
});
const audienceFilter_Tags = z.object({
  type: z.literal("tags"),
  id: z.string().optional(),
  cop: z.enum([
    "is",
    "is_not",
    "contains",
    "not_contains",
    "starts_with",
    "ends_with",
  ]),
  op: z.enum(["is", "contains", "starts_with", "ends_with"]),
  not: z.boolean(),
  value: z.string(),
});
export const messagingActivityCops = [
  "at_least_once",
  "at_least_n",
  "more_than_n",
  "at_most_n",
  "less_than_n",
  "zero_times",
  "exactly_n",
  "not_exactly_n",
] as const;

export const messagingActivityTimeframeSchema = z.object({
  op: z.literal("less_or_equal"),
  count: z.number().int().min(1),
});

// Standalone, not part of timeframeSchema, so the other date filters stay
// strict. Only order-placed accepts it.
export const betweenDaysTimeframeSchema = z.object({
  op: z.literal("between_days"),
  count: z.number().int().min(1),
  countTo: z.number().int().max(3650),
});

// After and before are strict. The chosen day is excluded.
export const orderPlacedTimeframeSchema = z
  .union([
    messagingActivityTimeframeSchema,
    z.object({ op: z.literal("greater"), date: z.string().date() }),
    z.object({ op: z.literal("less"), date: z.string().date() }),
    z.object({
      op: z.literal("between"),
      from: z.string().date(),
      to: z.string().date(),
    }),
    betweenDaysTimeframeSchema,
  ])
  .superRefine((timeframe, ctx) => {
    if (timeframe.op === "between" && timeframe.from > timeframe.to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["to"],
        message: "'to' must not be before 'from'",
      });
    }
    if (
      timeframe.op === "between_days" &&
      timeframe.count >= timeframe.countTo
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["countTo"],
        message: "'countTo' must be greater than 'count'",
      });
    }
  });

export const broadcastSubFilterFields = ["broadcast_id"] as const;
export type BroadcastSubFilterField = (typeof broadcastSubFilterFields)[number];

// The messaging-activity filters support a single broadcast_id sub-filter,
// matched exactly (op is always "is"). The UI picks a broadcast by name and stores its id, so
// the API/worker only ever deal with the id. Backend: queen-one/audiences#103.
const broadcastSubFilterSchema = z.object({
  field: z.literal("broadcast_id"),
  op: z.literal("is"),
  value: z.string().min(1),
});

export const connectorEventSubFilterFields = ["connector_event_name"] as const;
export type ConnectorEventSubFilterField =
  (typeof connectorEventSubFilterFields)[number];

// Connector-triggered sends stamp $.reporting.connector_event_name with the
// signal's event_id — tile-picker writes TriggerEventName (guardrails.go), which
// is ev.GetEventId() (app.go). So the UI picks an event by name and stores its
// **id**; the API/worker only ever deal with the id. Matched exactly (op is
// always "is"). Backend: audiences/internal/worker/processor/filter_rules_events.go.
const connectorEventSubFilterSchema = z.object({
  field: z.literal("connector_event_name"),
  op: z.literal("is"),
  value: z.string().min(1),
});

// Matched exactly against $.reporting.frame_id. The UI labels this "tile" — the
// product's word for a frame in the audience builder.
const frameSubFilterSchema = z.object({
  field: z.literal("frame_id"),
  op: z.literal("is"),
  value: z.string().min(1),
});

// A messaging activity filter's tile is the Broadcast tile, a connector tile or
// a basic tile, never more than one, so the sub-filter kinds share one slot.
const messagingActivitySubFilterSchema = z.union([
  broadcastSubFilterSchema,
  connectorEventSubFilterSchema,
  frameSubFilterSchema,
]);

export const isFullLink = (value: string) => {
  try {
    const url = new URL(value);
    return /^https?:$/.test(url.protocol) && url.hostname !== "";
  } catch {
    return false;
  }
};

const clickUrlSubFilterSchema = z
  .object({
    field: z.literal("click_url"),
    op: z.enum(["is", "contains"]),
    value: z
      .string()
      .min(1)
      .refine((v) => !/[?#\s]/.test(v), {
        message: "can't include ?, # or spaces",
      }),
    not: z.boolean().optional(),
  })
  .refine((f) => f.op !== "is" || isFullLink(f.value), {
    message: "is needs a full http(s) link",
    path: ["value"],
  });

const clickActivitySubFilterSchema = z.union([
  ...messagingActivitySubFilterSchema.options,
  clickUrlSubFilterSchema,
]);

// Text filters take the same sub-filters, behind a whitelist: anything shaped
// otherwise (null, a primitive, an unknown field, etc.) is dropped to undefined
// before validation
const textSubFilterSchema = <T extends z.ZodTypeAny>(
  schema: T,
  fields: readonly string[],
) =>
  z.preprocess((val) => {
    const field =
      val !== null && typeof val === "object"
        ? (val as { field?: unknown }).field
        : undefined;
    return typeof field === "string" && fields.includes(field)
      ? val
      : undefined;
  }, schema.optional());

const textActivitySubFilterSchema = textSubFilterSchema(
  messagingActivitySubFilterSchema,
  ["broadcast_id", "connector_event_name", "frame_id"],
);
const clickedTextSubFilterSchema = textSubFilterSchema(
  clickActivitySubFilterSchema,
  ["broadcast_id", "connector_event_name", "frame_id", "click_url"],
);

const clickActivitySubFiltersSchema = z
  .array(clickActivitySubFilterSchema)
  .max(2)
  .optional();

const audienceFilter_ReceivedEmail = z.object({
  type: z.literal("received-email"),
  id: z.string().optional(),
  cop: z.enum(messagingActivityCops).default("at_least_once"),
  op: z.enum(["greater_or_equal", "equal"]).default("greater_or_equal"),
  not: z.boolean().default(false),
  count: z.number().int().min(1).default(1),
  tileSlug: z.string().optional(),
  timeframe: messagingActivityTimeframeSchema.optional(),
  subFilter: messagingActivitySubFilterSchema.optional(),
});
const audienceFilter_OpenedEmail = z.object({
  type: z.literal("opened-email"),
  id: z.string().optional(),
  cop: z.enum(messagingActivityCops).default("at_least_once"),
  op: z.enum(["greater_or_equal", "equal"]).default("greater_or_equal"),
  not: z.boolean().default(false),
  count: z.number().int().min(1).default(1),
  tileSlug: z.string().optional(),
  timeframe: messagingActivityTimeframeSchema.optional(),
  subFilter: messagingActivitySubFilterSchema.optional(),
});
const audienceFilter_ClickedEmail = z.object({
  type: z.literal("clicked-email"),
  id: z.string().optional(),
  cop: z.enum(messagingActivityCops).default("at_least_once"),
  op: z.enum(["greater_or_equal", "equal"]).default("greater_or_equal"),
  not: z.boolean().default(false),
  count: z.number().int().min(1).default(1),
  tileSlug: z.string().optional(),
  timeframe: messagingActivityTimeframeSchema.optional(),
  subFilter: clickActivitySubFilterSchema.optional(),
  subFilters: clickActivitySubFiltersSchema,
});
const audienceFilter_ReceivedText = z.object({
  type: z.literal("received-text"),
  id: z.string().optional(),
  cop: z.enum(messagingActivityCops).default("at_least_once"),
  op: z.enum(["greater_or_equal", "equal"]).default("greater_or_equal"),
  not: z.boolean().default(false),
  count: z.number().int().min(1).default(1),
  tileSlug: z.string().optional(),
  timeframe: messagingActivityTimeframeSchema.optional(),
  subFilter: textActivitySubFilterSchema,
});
const audienceFilter_ClickedText = z.object({
  type: z.literal("clicked-text"),
  id: z.string().optional(),
  cop: z.enum(messagingActivityCops).default("at_least_once"),
  op: z.enum(["greater_or_equal", "equal"]).default("greater_or_equal"),
  not: z.boolean().default(false),
  count: z.number().int().min(1).default(1),
  tileSlug: z.string().optional(),
  timeframe: messagingActivityTimeframeSchema.optional(),
  subFilter: clickedTextSubFilterSchema,
  subFilters: clickActivitySubFiltersSchema,
});

export const geoCountryRegionCops = [
  "is",
  "is_not",
  "is_blank",
  "is_not_blank",
] as const;
export type GeoCountryRegionCop = (typeof geoCountryRegionCops)[number];

export const geoTextCops = [
  "is",
  "is_not",
  "is_blank",
  "is_not_blank",
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
] as const;
export type GeoTextCop = (typeof geoTextCops)[number];

const geoCountryRegionOps = z.enum(["is", "is_not_excl_blank"]);
const geoTextOps = z.enum([
  "is",
  "is_not_excl_blank",
  "contains",
  "does_not_contain_excl_blank",
  "starts_with",
  "ends_with",
]);

const audienceFilter_Country = z.object({
  type: z.literal("country"),
  id: z.string().optional(),
  cop: z.enum(geoCountryRegionCops),
  op: geoCountryRegionOps,
  not: z.boolean(),
  value: z.string(),
});
const audienceFilter_Region = z.object({
  type: z.literal("region"),
  id: z.string().optional(),
  cop: z.enum(geoCountryRegionCops),
  op: geoCountryRegionOps,
  not: z.boolean(),
  value: z.string(),
  countryCode: z.string().optional().default(""),
});
const audienceFilter_City = z.object({
  type: z.literal("city"),
  id: z.string().optional(),
  cop: z.enum(geoTextCops),
  op: geoTextOps,
  not: z.boolean(),
  value: z.string(),
  countryCode: z.string().optional().default(""),
});
const audienceFilter_PostalCode = z.object({
  type: z.literal("postal-code"),
  id: z.string().optional(),
  cop: z.enum(geoTextCops),
  op: geoTextOps,
  not: z.boolean(),
  value: z.string(),
  countryCode: z.string().optional().default(""),
});

const strictExclusionGeoOps = new Set([
  "is_not_excl_blank",
  "does_not_contain_excl_blank",
]);

export const productSubFilterFields = [
  "product_id",
  "category",
  "brand",
  "vendor",
  "tag",
  "collection",
] as const;
export type ProductSubFilterField = (typeof productSubFilterFields)[number];

const productSubFilterSchema = z.object({
  field: z.enum(productSubFilterFields),
  op: z.literal("is"),
  value: z.string().min(1),
  not: z.boolean().optional(),
});

export const cartValueCurrencies = ["native", "usd"] as const;
export type CartValueCurrency = (typeof cartValueCurrencies)[number];

export const cartValueWireOps = ["greater", "greater_or_equal"] as const;

const cartValueSubFilterSchema = z.object({
  field: z.literal("cart_value"),
  op: z.enum(cartValueWireOps),
  // Sent as a string even though it is numeric. Must be non-empty (ignoring
  // whitespace), finite, and non-negative; zero is allowed. validateCartValueAmount
  // in filter-utils mirrors these rules to render the inline error.
  value: z
    .string()
    .refine((v) => v.trim() !== "", { message: "requires a value" })
    .refine((v) => Number.isFinite(Number(v)), {
      message: "must be a finite number",
    })
    .refine((v) => Number(v) >= 0, { message: "must be >= 0" }),
  currency: z.enum(cartValueCurrencies),
  not: z.boolean().optional(),
});

// Cart value applies to order-placed and in-cart; product activity views keep
// the product-only sub-filter.
const orderPlacedSubFilterSchema = z.union([
  productSubFilterSchema,
  cartValueSubFilterSchema,
]);

export const maxVariantIds = 100;

const productIdEntrySchema = productSubFilterSchema.extend({
  field: z.literal("product_id"),
});

const variantEntrySchema = z.object({
  field: z.literal("variant_id"),
  op: z.literal("in"),
  values: z.array(z.string().min(1)).min(1).max(maxVariantIds),
  not: z.boolean().optional(),
});

// Both entries hold on the same order item, cart line or view. The API takes
// only a product entry plus a variant entry; the filter-utils writers keep that
// pair, so the schema doesn't refine it.
const productVariantSubFiltersSchema = z
  .array(z.union([productIdEntrySchema, variantEntrySchema]))
  .max(2);

export const viewedPageSubFilterFields = ["url"] as const;
export type ViewedPageSubFilterField =
  (typeof viewedPageSubFilterFields)[number];

const viewedPageSubFilterSchema = z.object({
  field: z.literal("url"),
  op: z.enum(["is", "contains", "starts_with"]),
  value: z.string().min(1),
  not: z.boolean().optional(),
});

export const revenueValueCops = [
  "more_than",
  "at_least",
  "at_most",
  "less_than",
  "exactly",
  "not_exactly",
] as const;
export type RevenueValueCop = (typeof revenueValueCops)[number];

export const revenueValueWireOps = [
  "greater",
  "greater_or_equal",
  "less",
  "less_or_equal",
  "equal",
  "not_equal",
] as const;
export type RevenueValueWireOp = (typeof revenueValueWireOps)[number];

// One wire op per cop with not:false — unlike cartValueCopToWire, whose lower
// bounds are negations and so need a table.
export const revenueValueCopOps: Record<RevenueValueCop, RevenueValueWireOp> = {
  more_than: "greater",
  at_least: "greater_or_equal",
  at_most: "less_or_equal",
  less_than: "less",
  exactly: "equal",
  not_exactly: "not_equal",
};

// The lower bounds are real ops, not `not` over an upper bound — negating the
// revenue bitmap would sweep in every user who never ordered.
const audienceFilter_RevenueValue = z.object({
  type: z.literal("revenue-value"),
  id: z.string().optional(),
  cop: z.enum(revenueValueCops).default("more_than"),
  op: z.enum(revenueValueWireOps).default("greater"),
  not: z.boolean().default(false),
  // The API canonicalizes this exactly — two decimals, or three where the
  // currency uses them — and echoes that form back.
  value: z
    .string()
    .refine((v) => v.trim() !== "", { message: "requires a value" })
    .refine((v) => Number.isFinite(Number(v)), {
      message: "must be a finite number",
    })
    .refine((v) => Number(v) >= 0, { message: "must be >= 0" }),
  currency: z.enum(cartValueCurrencies),
  timeframe: messagingActivityTimeframeSchema.optional(),
});

export const inCartCops = [
  "has_items",
  "no_items",
  "more_than_n",
  "less_than_n",
] as const;
export type InCartCop = (typeof inCartCops)[number];

const inCartWireOps = ["is", "greater", "less"] as const;
type InCartWireOp = (typeof inCartWireOps)[number];

export const inCartCopWire: Record<
  InCartCop,
  { op: InCartWireOp; not: boolean }
> = {
  has_items: { op: "is", not: false },
  no_items: { op: "is", not: true },
  more_than_n: { op: "greater", not: false },
  less_than_n: { op: "less", not: false },
};

export const inCartCopLabels: Record<InCartCop, string> = {
  has_items: "has items",
  no_items: "has no items",
  more_than_n: "more than",
  less_than_n: "fewer than",
};

const audienceFilter_InCart = z.object({
  type: z.literal("in-cart"),
  id: z.string().optional(),
  cop: z.enum(inCartCops),
  op: z.enum(inCartWireOps),
  not: z.boolean().default(false),
  value: z.string().optional(),
  subFilter: orderPlacedSubFilterSchema.optional(),
  subFilters: productVariantSubFiltersSchema.optional(),
});

const audienceFilter_OrderPlaced = z.object({
  type: z.literal("order-placed"),
  id: z.string().optional(),
  cop: z.enum(messagingActivityCops).default("at_least_once"),
  op: z.enum(["greater_or_equal", "equal"]).default("greater_or_equal"),
  not: z.boolean().default(false),
  count: z.number().int().min(1).default(1),
  timeframe: orderPlacedTimeframeSchema.optional(),
  subFilter: orderPlacedSubFilterSchema.optional(),
  subFilters: productVariantSubFiltersSchema.optional(),
});
const audienceFilter_ViewedProduct = z.object({
  type: z.literal("viewed-product"),
  id: z.string().optional(),
  cop: z.enum(messagingActivityCops).default("at_least_once"),
  op: z.enum(["greater_or_equal", "equal"]).default("greater_or_equal"),
  not: z.boolean().default(false),
  count: z.number().int().min(1).default(1),
  timeframe: messagingActivityTimeframeSchema.optional(),
  subFilter: productSubFilterSchema.optional(),
  subFilters: productVariantSubFiltersSchema.optional(),
});
const audienceFilter_HighIntentView = z.object({
  type: z.literal("high-intent-view"),
  id: z.string().optional(),
  cop: z.enum(messagingActivityCops).default("at_least_once"),
  op: z.enum(["greater_or_equal", "equal"]).default("greater_or_equal"),
  not: z.boolean().default(false),
  count: z.number().int().min(1).default(1),
  timeframe: messagingActivityTimeframeSchema.optional(),
  subFilter: productSubFilterSchema.optional(),
  subFilters: productVariantSubFiltersSchema.optional(),
});
const audienceFilter_ViewedPage = z.object({
  type: z.literal("viewed-page"),
  id: z.string().optional(),
  cop: z.enum(messagingActivityCops).default("at_least_once"),
  op: z.enum(["greater_or_equal", "equal"]).default("greater_or_equal"),
  not: z.boolean().default(false),
  count: z.number().int().min(1).default(1),
  timeframe: messagingActivityTimeframeSchema.optional(),
  subFilter: viewedPageSubFilterSchema.optional(),
});

export const audienceFiltersSchema = z.preprocess(
  normalizeAudienceFiltersInput,
  z.object({
    type: z.enum(["and"]),
    groups: z
      .array(
        z.object({
          id: z.string().optional(),
          type: z.enum(["or"]),
          filters: z
            .array(
              z.discriminatedUnion("type", [
                audienceFilter_ListInSchema,
                audienceFilter_Prospect,
                audienceFilter_EmailEligible,
                audienceFilter_SmsEligible,
                audienceFilter_Email,
                audienceFilter_Phone,
                audienceFilter_Metadata,
                audienceFilter_ProfileCreated,
                audienceFilter_Tags,
                audienceFilter_ReceivedEmail,
                audienceFilter_OpenedEmail,
                audienceFilter_ClickedEmail,
                audienceFilter_ReceivedText,
                audienceFilter_ClickedText,
                audienceFilter_OrderPlaced,
                audienceFilter_InCart,
                audienceFilter_RevenueValue,
                audienceFilter_ViewedProduct,
                audienceFilter_HighIntentView,
                audienceFilter_ViewedPage,
                audienceFilter_Country,
                audienceFilter_Region,
                audienceFilter_City,
                audienceFilter_PostalCode,
              ]),
            )
            .min(1)
            .superRefine((filters, ctx) => {
              filters.forEach((f, i) => {
                if (
                  "op" in f &&
                  "not" in f &&
                  strictExclusionGeoOps.has(f.op) &&
                  f.not
                ) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, "not"],
                    message: `'${f.op}' cannot be combined with not: true`,
                  });
                }
                // Each email-eligible reasoning value belongs to one side of the
                // filter. The enum already constrains the value set — this
                // rejects a value offered under the other side. (Refinement
                // lives here, not on the member, because discriminatedUnion
                // options can't be ZodEffects.)
                if (f.type === "email-eligible" && f.value) {
                  const sideValues: readonly string[] =
                    f.cop === "is"
                      ? emailEligibleSubscribedValues
                      : emailEligibleSuppressionValues;
                  if (!sideValues.includes(f.value)) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: [i, "value"],
                      message: `email eligibility value '${f.value}' is not valid when '${f.cop === "is" ? "is eligible" : "is not eligible"}'`,
                    });
                  }
                  if (
                    f.timeframe &&
                    emailEligibleUntimedValues.includes(f.value)
                  ) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: [i, "timeframe"],
                      message: `email eligibility value '${f.value}' has no date, so it cannot take a timeframe`,
                    });
                  }
                }
                if (f.type === "sms-eligible" && f.value) {
                  if (f.cop === "is") {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: [i, "value"],
                      message: `sms eligibility value '${f.value}' is not valid when 'is eligible'`,
                    });
                  }
                  if (
                    f.timeframe &&
                    smsEligibleUntimedValues.includes(f.value)
                  ) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: [i, "timeframe"],
                      message: `sms eligibility value '${f.value}' has no date, so it cannot take a timeframe`,
                    });
                  }
                }
                if (f.type === "in-cart") {
                  const wire = inCartCopWire[f.cop];
                  if (f.op !== wire.op || f.not !== wire.not) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: [i, "op"],
                      message: `'${f.cop}' requires op '${wire.op}' with not ${wire.not}`,
                    });
                  }
                  const value = f.value ?? "";
                  const isCount = f.op !== "is";
                  if (isCount ? !/^\d+$/.test(value) : value !== "") {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: [i, "value"],
                      message: isCount
                        ? "item count must be a whole number, 0 or more"
                        : "this operator takes no value",
                    });
                  } else if (f.op === "less" && Number(value) < 2) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: [i, "value"],
                      message: "fewer than needs 2 or more",
                    });
                  }
                }
                if (
                  f.type === "revenue-value" &&
                  f.op !== revenueValueCopOps[f.cop]
                ) {
                  ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [i, "op"],
                    message: `'${f.cop}' requires op '${revenueValueCopOps[f.cop]}', got '${f.op}'`,
                  });
                }
                if (f.type === "metadata") {
                  const isNumericCop = f.cop.startsWith("num_");
                  const isBlankCop =
                    f.cop === "is_blank" || f.cop === "is_not_blank";
                  if (!isNumericCop && !isBlankCop && !f.value.trim()) {
                    ctx.addIssue({
                      code: z.ZodIssueCode.custom,
                      path: [i, "value"],
                      message: "metadata filter requires a value",
                    });
                  }
                }
              });
            }),
        }),
      )
      .min(1),
  }),
);

export const audienceCountSchema = z.object({
  audiences: z.record(z.string(), z.number()),
});

export type AudienceCount = z.infer<typeof audienceCountSchema>;

/**
 * Response for GET /{siteId}/audience_builder/reach — the deduplicated union of
 * the selected audiences. `total` is a proto int64 serialized as a JSON string,
 * so it is coerced to a number. `pending` lists audiences whose membership the
 * segment worker has not yet computed; while non-empty the union is incomplete,
 * so the UI shows a warning instead of the estimate.
 */
export const audienceReachSchema = z.object({
  total: z.coerce.number(),
  pending: z
    .array(z.object({ id: z.string(), name: z.string() }))
    .optional()
    .default([]),
});

export type AudienceReach = z.infer<typeof audienceReachSchema>;

export const filtersEstimateSchema = z.object({
  groups: z
    .array(
      z.object({
        filters: z
          .array(
            z.object({
              count: z.coerce.number().default(0),
              computed: z.boolean().default(false),
            }),
          )
          .default([]),
      }),
    )
    .default([]),
  total: z.coerce.number().default(0),
  totalComputed: z.boolean().default(false),
});

export type FiltersEstimate = z.infer<typeof filtersEstimateSchema>;

export const audienceProcessingStateEnum = z.enum([
  "AUDIENCE_PROCESSING_STATE_UNSPECIFIED",
  "AUDIENCE_PROCESSING_STATE_PENDING",
  "AUDIENCE_PROCESSING_STATE_PROCESSING",
  "AUDIENCE_PROCESSING_STATE_READY",
]);
export type AudienceProcessingState = z.infer<
  typeof audienceProcessingStateEnum
>;

export const audienceSchema = z.object({
  id: z.string(),
  siteId: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  name: z.string(),
  filters: audienceFiltersSchema,
  count: z.coerce.number().optional(),
  state: audienceProcessingStateEnum
    .catch("AUDIENCE_PROCESSING_STATE_UNSPECIFIED")
    .optional(),
});

export const audienceCreateSchema = audienceSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

export type Audience = z.infer<typeof audienceSchema>;
export type AudienceCreate = z.infer<typeof audienceCreateSchema>;
export type AudienceFilter_ListInSchema = z.infer<
  typeof audienceFilter_ListInSchema
>;
export type AudienceFilter_Prospect = z.infer<typeof audienceFilter_Prospect>;
export type AudienceFilter_EmailEligible = z.infer<
  typeof audienceFilter_EmailEligible
>;
export type AudienceFilter_SmsEligible = z.infer<
  typeof audienceFilter_SmsEligible
>;
export type AudienceFilter_Email = z.infer<typeof audienceFilter_Email>;
export type AudienceFilter_Phone = z.infer<typeof audienceFilter_Phone>;
export type AudienceFilter_Metadata = z.infer<typeof audienceFilter_Metadata>;
export type AudienceFilter_ProfileCreated = z.infer<
  typeof audienceFilter_ProfileCreated
>;
export type AudienceFilter_Tags = z.infer<typeof audienceFilter_Tags>;
export type AudienceFilter_ReceivedEmail = z.infer<
  typeof audienceFilter_ReceivedEmail
>;
export type AudienceFilter_OpenedEmail = z.infer<
  typeof audienceFilter_OpenedEmail
>;
export type AudienceFilter_ClickedEmail = z.infer<
  typeof audienceFilter_ClickedEmail
>;
export type AudienceFilter_ReceivedText = z.infer<
  typeof audienceFilter_ReceivedText
>;
export type AudienceFilter_ClickedText = z.infer<
  typeof audienceFilter_ClickedText
>;
export type AudienceFilter_OrderPlaced = z.infer<
  typeof audienceFilter_OrderPlaced
>;
export type AudienceFilter_InCart = z.infer<typeof audienceFilter_InCart>;
export type AudienceFilter_RevenueValue = z.infer<
  typeof audienceFilter_RevenueValue
>;
export type AudienceFilter_ViewedProduct = z.infer<
  typeof audienceFilter_ViewedProduct
>;
export type AudienceFilter_HighIntentView = z.infer<
  typeof audienceFilter_HighIntentView
>;
export type AudienceFilter_ViewedPage = z.infer<
  typeof audienceFilter_ViewedPage
>;
export type AudienceFilter_Country = z.infer<typeof audienceFilter_Country>;
export type AudienceFilter_Region = z.infer<typeof audienceFilter_Region>;
export type AudienceFilter_City = z.infer<typeof audienceFilter_City>;
export type AudienceFilter_PostalCode = z.infer<
  typeof audienceFilter_PostalCode
>;
export type AudienceProductSubFilter = z.infer<typeof productSubFilterSchema>;
export type AudienceCartValueSubFilter = z.infer<
  typeof cartValueSubFilterSchema
>;
export type AudienceVariantEntry = z.infer<typeof variantEntrySchema>;
export type AudienceOrderPlacedSubFilter = z.infer<
  typeof orderPlacedSubFilterSchema
>;
export type AudienceViewedPageSubFilter = z.infer<
  typeof viewedPageSubFilterSchema
>;
export type AudienceBroadcastSubFilter = z.infer<
  typeof broadcastSubFilterSchema
>;
export type AudienceConnectorEventSubFilter = z.infer<
  typeof connectorEventSubFilterSchema
>;
export type AudienceFrameSubFilter = z.infer<typeof frameSubFilterSchema>;
export type AudienceMessagingSubFilter = z.infer<
  typeof clickActivitySubFilterSchema
>;
export type AudienceFilter_MessagingActivity =
  | AudienceFilter_ReceivedEmail
  | AudienceFilter_OpenedEmail
  | AudienceFilter_ClickedEmail
  | AudienceFilter_ReceivedText
  | AudienceFilter_ClickedText;
export type AudienceFilter_ProductActivity =
  | AudienceFilter_OrderPlaced
  | AudienceFilter_ViewedProduct
  | AudienceFilter_HighIntentView;
export type MessagingActivityCop = (typeof messagingActivityCops)[number];
export type AudienceTimeframe = z.infer<typeof timeframeSchema>;
export type BetweenDaysTimeframe = z.infer<typeof betweenDaysTimeframeSchema>;
export type OrderPlacedTimeframe = z.infer<typeof orderPlacedTimeframeSchema>;

export const audienceListSearchSchema = z.object({
  search: z.string().optional(),
  page: z.coerce.number().int().gte(1).optional(),
  pageSize: z.coerce.number().int().gte(10).lte(30).optional(),
  successAudience: z.string().optional(),
  successAction: z.enum(["created", "updated"]).optional(),
});

export type AudiencesSearch = z.infer<typeof audienceListSearchSchema>;

export type AudienceEdit = z.infer<typeof audienceCreateSchema>;
export type AudienceFilters = z.infer<typeof audienceFiltersSchema>;
export type AudienceFilter =
  Audience["filters"]["groups"][number]["filters"][number];
export type AudienceFilterType = AudienceFilter["type"];

export const consentSchema = z.object({
  consented: z.boolean(),
  source: z.string(),
  updatedAt: z.string().datetime().nullable(),
});

export type Consent = z.infer<typeof consentSchema>;

export const complaintSchema = z.object({
  complained: z.boolean(),
  updatedAt: z.string().datetime().nullable(),
});

export type Complaint = z.infer<typeof complaintSchema>;

export const hardBounceSchema = z.object({
  hardBounced: z.boolean(),
  reason: z.string(),
  updatedAt: z.string().datetime().nullable(),
});

export type HardBounce = z.infer<typeof hardBounceSchema>;

export const addressSchema = z.object({
  city: z.string().optional(),
  regionCode: z.string().optional(),
  countryCode: z.string().optional(),
  postalCode: z.string().optional(),
  updatedAt: z.string().datetime().optional(),
});

export type Address = z.infer<typeof addressSchema>;

export const audienceUserSchema = z.object({
  id: z.string(),
  email: z.string(),
  phone: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  customer: z.boolean(),
  emailConsented: consentSchema.optional(),
  smsConsented: consentSchema.optional(),
  hardBounced: hardBounceSchema.optional(),
  complained: complaintSchema.optional(),
  metadata: z.record(z.string(), z.any()).nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  addedAt: z.string().datetime().nullish(),
  addressUserProvided: addressSchema.nullish(),
  addressNetworkDerived: addressSchema.nullish(),
});

export type AudienceUser = z.infer<typeof audienceUserSchema>;

export const audienceMemberSearchSchema = z.object({
  nextCursor: z.string().optional(),
  search: z.string().optional(),
});

export type AudienceMembersSearchInfo = z.infer<
  typeof audienceMemberSearchSchema
>;

export const jobStatusEnum = z.enum([
  "JOB_STATUS_UNSPECIFIED",
  "JOB_STATUS_PENDING",
  "JOB_STATUS_RUNNING",
  "JOB_STATUS_COMPLETED",
  "JOB_STATUS_FAILED",
  "JOB_STATUS_CANCELLED",
]);
export type JobStatus = z.infer<typeof jobStatusEnum>;

export const audienceExportJobSchema = z.object({
  name: z.string().optional(),
  audience: z.object({ id: z.string(), name: z.string() }).nullish(),
  job: z.object({
    id: z.string(),
    listId: z.string().optional(),
    audienceId: z.string().optional(),
    type: z.literal("JOB_TYPE_EXPORT"),
    status: jobStatusEnum,
    result: z
      .object({
        totalRows: z.coerce.number(),
        successfulRows: z.coerce.number(),
        fileUrl: z.string(),
      })
      .nullish(),
    createdAt: z.string(),
    updatedAt: z.string(),
  }),
});
export type AudienceExportJob = z.infer<typeof audienceExportJobSchema>;

export const audienceExportsSearchSchema = z.object({
  search: z.string().optional(),
  page: z.coerce.number().int().gte(1).optional(),
  pageSize: z.coerce.number().int().gte(10).lte(30).optional(),
});
export type AudienceExportsSearch = z.infer<typeof audienceExportsSearchSchema>;

export const userAudiencesResponseSchema = z.object({
  audiences: z.array(
    z.object({
      audienceId: z.string(),
      name: z.string(),
      memberCount: z.string(),
    }),
  ),
});

export type UserAudiencesResponse = z.infer<typeof userAudiencesResponseSchema>;

export type UserAudiencesLookup =
  | { kind: "email"; value: string }
  | { kind: "phone"; value: string }
  | { kind: "userId"; value: string };
